import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DashboardMetricCard from './DashboardMetricCard';

const TFRCardDashboard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('unknown');
  const [tfrData, setTfrData] = useState([]);
  const [dataNote, setDataNote] = useState('');

  useEffect(() => {
    const fetchTFR = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['No location set']);
        setLoading(false);
        return;
      }

      // Only show loading on first load
      if (tfrData.length === 0 && severity === 'unknown') {
        setLoading(true);
      }
      try {
        setDataNote('');
        const res = await fetch('/API/tfr-proxy.php?t=' + Date.now());
        if (!res.ok) throw new Error(`TFR API returned ${res.status}`);
        const data = await res.json();
        const tfrs = data.TFRs || [];
        const updatedAt = data.updated ? new Date(data.updated) : null;
        const updatedAtMs = updatedAt?.getTime();
        const dataAgeMinutes = Number.isFinite(updatedAtMs)
          ? Math.max(0, Math.round((Date.now() - updatedAtMs) / 60000))
          : null;
        const stale = !Number.isFinite(dataAgeMinutes) || dataAgeMinutes > 60;

        // Calculate distance for each TFR
        const haversineNm = (lat1, lon1, lat2, lon2) => {
          const toRad = deg => deg * (Math.PI / 180);
          const R = 6371;
          const dLat = toRad(lat2 - lat1);
          const dLon = toRad(lon2 - lon1);
          const a = Math.sin(dLat / 2) ** 2 +
                    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                    Math.sin(dLon / 2) ** 2;
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return R * c * 0.539957;
        };

        const tfrList = tfrs.map(tfr => {
          const tfrLat = parseFloat(tfr.geoLat) || 0;
          const tfrLon = parseFloat(tfr.geoLong) || 0;
          const distance = haversineNm(lat, lon, tfrLat, tfrLon);
          const effective = tfr.EffectiveDateTime ? new Date(tfr.EffectiveDateTime) : null;
          const expires = tfr.ExpirationDateTime ? new Date(tfr.ExpirationDateTime) : null;
          return {
            name: tfr.Activity || 'TFR',
            type: tfr.NOTAMID || '',
            distance,
            effective,
            expires
          };
        });

        const now = new Date();
        const activeNearby = tfrList
          .filter(tfr => tfr.distance <= 25)
          .filter(tfr => {
            const effectiveMs = tfr.effective?.getTime();
            const expiresMs = tfr.expires?.getTime();
            if (!Number.isFinite(effectiveMs) || !Number.isFinite(expiresMs)) return false;
            return now >= tfr.effective && now <= tfr.expires;
          })
          .sort((a, b) => a.distance - b.distance);
        const nearby = activeNearby;
        setTfrData(nearby);
        if (stale) {
          setDataNote('TFR source timestamp is older than expected');
        }

        if (nearby.length === 0) {
          setSeverity('go');
          onUpdate && onUpdate('go', []);
        } else {
          const hasVeryClose = nearby.some(tfr => tfr.distance <= 5);
          if (hasVeryClose) {
            setSeverity('no-go');
            onUpdate && onUpdate('no-go', [`${nearby[0].name || 'TFR'} is within ${nearby[0].distance.toFixed(1)} NM`]);
          } else {
            setSeverity('caution');
            onUpdate && onUpdate('caution', [`${nearby[0].name || 'Nearest TFR'} is ${nearby[0].distance.toFixed(1)} NM away`]);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('TFR fetch failed:', err);
        setSeverity('advisory');
        setDataNote('TFR data unavailable');
        onUpdate && onUpdate('advisory', ['TFR data unavailable']);
        setLoading(false);
      }
    };

    fetchTFR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  const primaryMetric = {
    value: tfrData.length,
    label: tfrData.length === 1 ? 'TFR within 25 NM' : 'TFRs within 25 NM'
  };

  const secondaryMetrics = tfrData.length > 0 ? [
    { label: 'Nearest', value: `${tfrData[0].distance.toFixed(1)} NM`, highlight: tfrData[0].distance <= 5 }
  ] : [];

  const statusMessage = severity === 'go'
    ? 'No TFRs within 25 NM'
    : severity === 'advisory'
    ? (dataNote || 'TFR data unavailable')
    : severity === 'caution'
    ? 'TFR nearby - check details'
    : severity === 'no-go'
    ? 'TFR conflict - do not fly'
    : 'Checking TFRs...';

  return (
    <DashboardMetricCard
      title="TFRs"
      icon={BlockIcon}
      status={severity}
      primaryMetric={primaryMetric}
      secondaryMetrics={secondaryMetrics}
      statusMessage={statusMessage}
      loading={loading}
      minHeight={112}
    >
      {dataNote && severity === 'go' && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
          {dataNote}
        </Typography>
      )}
      {tfrData.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Nearby TFRs
          </Typography>
          <List dense>
            {tfrData.slice(0, 5).map((tfr, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemText
                  primary={tfr.name || `TFR #${idx + 1}`}
                  secondary={`${tfr.distance.toFixed(1)} NM away • ${tfr.type || 'Unknown type'}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
          {tfrData.length > 5 && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              +{tfrData.length - 5} more TFRs
            </Typography>
          )}
        </Box>
      )}
    </DashboardMetricCard>
  );
};

export default TFRCardDashboard;
